import { Fragment } from 'react';
import type {
  ColumnOrderColumnEnum,
  UISandboxMenuSection as UIMenuSandboxSectionTypes,
} from '@creoate/virgo-content-api';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';

import useVirgoContent from 'hooks/useVirgoContent';

import MenuTitle from 'components/Header/TopMenu/components/MenuTitle';
import BackButton from './BackButton';
import TopMenuMobileLink from './TopMenuMobileLink';
import BannersImage from '../components/BannersImage';

import { INavLink } from '../utils/types';

type Props = { selectedNavLinkItem: INavLink; handleBack: () => void };

const UIMenuSandboxSection = ({ selectedNavLinkItem, handleBack }: Props) => {
  const { topMenu } = useVirgoContent();

  const selectedMenu = topMenu?.filter(
    (menu, index) => index === selectedNavLinkItem?.index && menu?.component === selectedNavLinkItem?.component
  )?.[0] as UIMenuSandboxSectionTypes;

  return (
    <>
      <BackButton handleOnClick={handleBack} label={selectedNavLinkItem?.label} />

      <Box sx={{ height: '100%', overflowY: 'auto' }}>
        {selectedMenu?.columnsOrder?.map((item) => {
          if ((item.column as `${ColumnOrderColumnEnum}`) === 'Categories') {
            const { title } = selectedMenu.categoriesColumn;
            return (
              <Fragment key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-categories`}>
                <Stack sx={{ padding: '0 21px' }}>
                  {title && <MenuTitle title={title} />}

                  {selectedMenu?.categoriesColumn?.categories?.map((category, index) => (
                    <TopMenuMobileLink
                      label={category?.name}
                      href={`/product-category/${category?.slug}`}
                      key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-category-${index + 1}`}
                    />
                  ))}

                  {selectedMenu?.categoriesColumn?.collections?.map((collection, index) => (
                    <TopMenuMobileLink
                      label={collection?.name}
                      href={`/collections/${collection?.slug}`}
                      key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-collection-${index + 1}`}
                    />
                  ))}

                  {selectedMenu?.categoriesColumn?.links?.map((link, index) => (
                    <TopMenuMobileLink
                      href={link?.url}
                      label={link?.label}
                      key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-link-${index + 1}`}
                    />
                  ))}
                </Stack>
                <Divider />
              </Fragment>
            );
          }

          if ((item.column as `${ColumnOrderColumnEnum}`) === 'Products') {
            const { title } = selectedMenu.productsColumn;
            return (
              <Fragment key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-products`}>
                <Stack sx={{ padding: '0 21px' }}>
                  {title && <MenuTitle title={title} />}

                  {selectedMenu?.productsColumn?.products?.map((product, index) => (
                    <TopMenuMobileLink
                      label={product?.name}
                      href={`/product/${product?.slug}`}
                      key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-product-${index + 1}`}
                    />
                  ))}

                  {selectedMenu?.productsColumn?.links?.map((link, index) => (
                    <TopMenuMobileLink
                      href={link?.url}
                      label={link?.label}
                      key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-product-link-${index + 1}`}
                    />
                  ))}
                </Stack>
                <Divider />
              </Fragment>
            );
          }

          if ((item.column as `${ColumnOrderColumnEnum}`) === 'Wholesalers') {
            const { title } = selectedMenu.wholesalersColumn;
            return (
              <Fragment key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-wholesalers`}>
                <Stack sx={{ padding: '0 21px' }}>
                  {title && <MenuTitle title={title} />}

                  {selectedMenu?.wholesalersColumn?.wholesalers?.map((wholesaler, index) => (
                    <TopMenuMobileLink
                      label={wholesaler?.brandName}
                      href={`/store/${wholesaler?.slug}`}
                      key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-wholesaler-${index + 1}`}
                    />
                  ))}

                  {selectedMenu?.wholesalersColumn?.links?.map((link, index) => (
                    <TopMenuMobileLink
                      href={link?.url}
                      label={link?.label}
                      key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-wholesaler-link-${index + 1}`}
                    />
                  ))}
                </Stack>
                <Divider />
              </Fragment>
            );
          }

          if ((item.column as `${ColumnOrderColumnEnum}`) === 'Banners') {
            return (
              <Fragment key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-banners`}>
                <Stack sx={{ padding: '0 21px' }}>
                  {selectedMenu?.bannersColumn?.banner?.desktop?.map((bannerImage) => (
                    <BannersImage
                      alt={bannerImage?.link}
                      href={bannerImage?.link}
                      src={bannerImage?.image?.url}
                      key={`ui.sandbox-menu-section-${selectedNavLinkItem?.index}-banner`}
                      component={
                        selectedNavLinkItem?.label?.toLowerCase() === 'trending' ? 'menuSection' : 'menuSandboxSection'
                      }
                    />
                  ))}
                </Stack>
                <Divider />
              </Fragment>
            );
          }

          return null;
        })}
      </Box>
    </>
  );
};

export default UIMenuSandboxSection;
